import axiosInstance from "@/utils/axiosConfig";
import Article from "@/models/Article";
import axios, { AxiosError } from 'axios';
import { ErrorResponse } from '@/types/ErrorResponse';

const API_URL = '/article'; 

// Endpoints statiques pour les différentes actions
const ENDPOINTS = {
    ARTICLES: '/article/all_articles/',  // Endpoint DataTable pour fetchArticles
    ARTICLES_CONSOMME: '/article/all_articles_Consommes/',
    ARTICLES_EXPORT: '/article/all_articles/',
    ARTICLES_CONSOMME_EXPORT: '/article/all_articles_Consommes/'
};

// Types pour les réponses DataTable
interface DataTableResponse {
    data: any[];
    totalRows: number;
    recordsFiltered: number;
    recordsTotal: number;
    draw?: number;
}

// Helper pour créer FormData de manière optimisée
const createArticleFormData = (article: Article): FormData => {
    const formData = new FormData();
    
    // Champs obligatoires
    formData.append('N_facture', article.N_facture);
    formData.append('designation', article.designation);
    
    // Champs optionnels avec valeurs par défaut
    const optionalFields = {
        date_achat: article.date_achat?.toString() || '',
        prix_achat: article.prix_achat?.toString() || '',
        qte_recue: article.qte_recue?.toString() || '',
        numero_serie: article.numero_serie || '',
        numero_comptable: article.numero_comptable || '',
        couleur: article.couleur || '',
        poids: article.poids?.toString() || '',
        volume: article.volume?.toString() || '',
        langueur: article.langueur?.toString() || '',
        hauteur: article.hauteur?.toString() || '',
        largeur: article.largeur?.toString() || '',
        produit: article.produit?.toString() || '',
        fournisseur: article.fournisseur?.toString() || '',
        marque: article.marque?.toString() || '',
        nature: article.nature?.toString() || ''
    };
    
    Object.entries(optionalFields).forEach(([key, value]) => {
        formData.append(key, value);
    });
    
    // Gestion des fichiers
    if (article.image?.length) {
        formData.append('image', article.image[0]);
    }
    
    const attachments = [
        { key: 'attachement1', file: article.attachement1 },
        { key: 'attachement2', file: article.attachement2 },
        { key: 'attachement3', file: article.attachement3 }
    ];
    
    attachments.forEach(({ key, file }) => {
        if (file instanceof File) {
            formData.append(key, file);
        }
    });
    
    return formData;
};

// Helper pour gérer les erreurs de manière cohérente
const handleServiceError = (error: unknown): ErrorResponse => {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        return {
            status: axiosError.response?.status ?? 500,
            message: (axiosError.response?.data as { message?: string })?.message ?? axiosError.message ?? 'Une erreur est survenue'
        };
    }
    return {
        status: 500,
        message: 'Une erreur inattendue est survenue'
    };
};

// Helper pour formater les réponses DataTable
const formatDataTableResponse = (response: any): DataTableResponse => ({
    data: response.data?.data || response.data?.results || [],
    totalRows: response.data?.recordsFiltered || response.data?.count || 0,
    recordsFiltered: response.data?.recordsFiltered || response.data?.count || 0,
    recordsTotal: response.data?.recordsTotal || response.data?.count || 0,
    draw: response.data?.draw
});

const ArticleService = {
    /**
     * Créer des articles via import de fichier
     */
    async createwithImport(file: File[]): Promise<number | ErrorResponse> {
        try {
            if (!file?.length) {
                return {
                    status: 400,
                    message: 'Aucun fichier fourni'
                };
            }

            const formData = new FormData();
            formData.append('file', file[0]);

            const response = await axiosInstance.post(`${API_URL}/import/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            return response.status;
        } catch (error) {
            return handleServiceError(error);
        }
    },

    /**
     * Créer un nouvel article
     */
    async create(article: Article): Promise<number | ErrorResponse> {
        try {
            // Validation des champs obligatoires
            if (!article.N_facture || !article.designation) {
                return {
                    status: 400,
                    message: 'Les champs N_facture et designation sont obligatoires'
                };
            }

            const formData = createArticleFormData(article);

            const response = await axiosInstance.post(`${API_URL}/create/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            return response.status;
        } catch (error) {
            return handleServiceError(error);
        }
    },



    /**
     * Récupérer tous les articles avec paramètres DataTable
     */
    async getAll(params: string): Promise<DataTableResponse | ErrorResponse> {
        try {
            const url = `${ENDPOINTS.ARTICLES}${params}`;
            const response = await axiosInstance.get(url);
            return formatDataTableResponse(response);
        } catch (error) {
            return handleServiceError(error);
        }
    },


    /**
     * Récupérer tous les articles consommés avec paramètres DataTable
     */
    async getAllConsomme(url: string): Promise<any> {
        try {
            // Construire l'URL complète
            let fullUrl;
            if (url.startsWith('http')) {
                fullUrl = url;
            } else {
                // Retirer le ? du début de url s'il existe
                const cleanUrl = url.startsWith('?') ? url.substring(1) : url;
                fullUrl = `${ENDPOINTS.ARTICLES_CONSOMME}?${cleanUrl}`;
            }
            
            // Détecter si c'est un export (Excel/CSV)
            const isExport = url.includes('export=excel') || url.includes('export=csv');
            
            // Si c'est un export, on attend un blob (fichier binaire)
            const response = await axiosInstance.get(fullUrl, {
                ...(isExport && { responseType: 'blob' })
            });
            
            // Si c'est un export, retourner directement la réponse blob
            if (isExport) {
                return response;
            }
            
            // Sinon, retourner les données formatées pour le DataTable
            return formatDataTableResponse(response);
        } catch (error) {
            return handleServiceError(error);
        }
    },

    /**
     * Récupérer un article par son ID
     */
    async getById(id: number): Promise<Article | ErrorResponse> {
        try {
            if (!id || id <= 0) {
                return {
                    status: 400,
                    message: 'ID invalide'
                };
            }

            const response = await axiosInstance.get(`${API_URL}/get_article/${id}/`);
            return response.data as Article;
        } catch (error) {
            return handleServiceError(error);
        }
    },

    /**
     * Supprimer un article par son ID
     */
    async supprimer(id: number): Promise<number | ErrorResponse> {
        try {
            if (!id || id <= 0) {
                return {
                    status: 400,
                    message: 'ID invalide'
                };
            }

            const response = await axiosInstance.delete(`${API_URL}/supprimer/${id}/`);
            return response.status;
        } catch (error) {
            return handleServiceError(error);
        }
    },

    /**
     * Mettre à jour un article
     */
    async update({ id, article }: { id: number, article: Article }): Promise<number | ErrorResponse> {
        try {
            if (!id || id <= 0) {
                return {
                    status: 400,
                    message: 'ID invalide'
                };
            }

            if (!article.N_facture || !article.designation) {
                return {
                    status: 400,
                    message: 'Les champs N_facture et designation sont obligatoires'
                };
            }

            const formData = createArticleFormData(article);

            const response = await axiosInstance.put(`${API_URL}/update_article/${id}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            return response.status;
        } catch (error) {
            return handleServiceError(error);
        }
    },
    /**
     * Exporter tous les articles en fichier
     */
    async extractAll(params: string): Promise<any> {
        try {
            const url = `${ENDPOINTS.ARTICLES_EXPORT}${params}`;
            const response = await axiosInstance.get(url, {
                responseType: 'blob'
            });
            return response;
        } catch (error) {
            return handleServiceError(error);
        }
    },

    /**
     * Exporter tous les articles consommés en fichier
     */
    async extractAllConsommes(params: string): Promise<any> {
        try {
            const url = `${ENDPOINTS.ARTICLES_CONSOMME_EXPORT}${params}`;
            const response = await axiosInstance.get(url, {
                responseType: 'blob'
            });
            return response;
        } catch (error) {
            return handleServiceError(error);
        }
    },


    

};

export default ArticleService;
